import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listProfileTypes, mapApiProfileTypeToUi } from '../../../services/profileTypes.js';

/** Four rows of three on a desktop grid, before the user has to ask for more. */
const DEFAULT_PAGE_SIZE = 12;

/**
 * The Unified Profile types the caller may see, paged and searchable.
 *
 * Pages ACCUMULATE rather than replace: the hub is a card grid, and a grid that
 * swaps its contents when you ask for more is a pager wearing the wrong
 * clothes. "Show more" should grow what you are already looking at — the same
 * behaviour the post-login workspace selector has.
 *
 * A new query resets to page one and discards what was loaded. Appending search
 * results to unfiltered ones would show rows that do not match the query, which
 * is worse than a moment of emptiness.
 */
export function useProfileTypes({ pageSize = DEFAULT_PAGE_SIZE, search } = {}) {
  const [types, setTypes] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  // Serialized so a fresh object literal from the caller does not refetch on
  // every render — the QUERY is what should drive one.
  const query = search?.query ?? '';

  const fetchPage = useCallback(
    async (targetPage) => {
      const requestId = ++fetchIdRef.current;
      const isFirst = targetPage === 1;
      if (isFirst) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      try {
        const { body, meta } = await listProfileTypes({
          page: targetPage,
          pageSize,
          ...(query ? { search: { query } } : {}),
        });
        if (requestId !== fetchIdRef.current) return;

        const mapped = (body?.items ?? []).map(mapApiProfileTypeToUi);
        setTypes((current) => (isFirst ? mapped : [...current, ...mapped]));
        setHasMore(Boolean(meta?.has_next));
        setPage(targetPage);
      } catch (err) {
        if (requestId !== fetchIdRef.current) return;
        if (isFirst) setTypes([]);
        setHasMore(false);
        setError(err?.message ?? 'Failed to load Unified Profile types');
      } finally {
        if (requestId === fetchIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [pageSize, query],
  );

  useEffect(() => {
    fetchPage(1);
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(page + 1);
  }, [fetchPage, hasMore, loading, loadingMore, page]);

  const refetch = useCallback(() => fetchPage(1), [fetchPage]);

  return useMemo(
    () => ({ types, loading, loadingMore, hasMore, loadMore, error, refetch }),
    [types, loading, loadingMore, hasMore, loadMore, error, refetch],
  );
}

export default useProfileTypes;
